import React from 'react'
import { motion } from 'motion/react'

interface CollapsingCardListProps<T> {
    items: T[]
    /**
     * Show only this many items until the user expands. Omit to show them all
     * — the control never appears in that case.
     */
    initialLimit?: number
    /** Renders one item. Responsible for its own `key`. */
    renderItem: (item: T, index: number) => React.ReactNode
    /** Classes for the list container, so each surface keeps its own spacing. */
    className?: string
    /** Classes for the show more/less control, likewise. */
    toggleClassName?: string
}

/**
 * One list of cards that collapses past a cap, shared by every surface in the
 * Genie chat that renders a set of checkins — `list_checkins` results, the
 * proposal panels, and the batch of checkins a commit wrote. They used to be
 * three implementations with three sets of button copy; this is the one.
 *
 * The copy is "Show N more" / "Show less", counting what's still hidden rather
 * than the whole list.
 */
function CollapsingCardList<T>({
    items,
    initialLimit,
    renderItem,
    className,
    toggleClassName = 'mt-3 text-sm text-amber-800 hover:text-amber-900 cursor-pointer',
}: CollapsingCardListProps<T>) {
    const [expanded, setExpanded] = React.useState(false)

    const isCollapsible = initialLimit !== undefined && items.length > initialLimit
    const visibleItems = isCollapsible && !expanded ? items.slice(0, initialLimit) : items
    const hiddenCount = items.length - visibleItems.length

    return (
        <>
            {items.length > 0 && (
                <div className={className}>
                    {visibleItems.map(renderItem)}
                </div>
            )}
            {isCollapsible && (
                <motion.button
                    type="button"
                    onClick={() => setExpanded(e => !e)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className={toggleClassName}
                >
                    {expanded ? 'Show less' : `Show ${hiddenCount} more`}
                </motion.button>
            )}
        </>
    )
}

export default CollapsingCardList
